import { ComponentRef, Injectable, inject } from '@angular/core';
import { assert } from 'src/app/core/assert/assert';
import { DragActiveEntryCSSClass } from '../../../../../core/constants-repository/sortable-rows.constants';
import { TodoEntryModel } from '../../../../../core/models/todo-planner/todo-entry.model';
import { SortableEntriesService } from '../../../../../core/services/features/sortable-elements/sortable-entries.service';
import { ShiveRenderService } from '../../../../../core/services/rendering/shive-render.service';
import { DateTimeHelper } from '../../../../calendar/util/date-time-helper';
import {
  AddToDoButtonWrapperCSSClass,
  CanDropCSSClass,
  DropTargetCSSClass,
  TodoContainerDateAttribute,
  TodoEntryWrapperCSSClass,
} from '../../../utils/todo-planner.constants';
import { TodoEntryComponent } from '../../todo-entry.component';
import { TodoEntryCrudService } from '../todo-entry-crud.service';
import { TodoEntryService } from '../todo-entry.service';

@Injectable({
  providedIn: 'root',
})
export class TodoEntrySharedDragAndDropService {
  public session: TodoEntryDragSession | null = null;

  // DI
  private readonly shiveRenderService = inject(ShiveRenderService);
  private readonly todoEntryService = inject(TodoEntryService);
  private readonly todoEntryCrudService = inject(TodoEntryCrudService);
  private readonly sortableEntriesService = inject(SortableEntriesService);

  public switchedLane(): boolean {
    const oldContainer = this.getContainer(true);
    const newContainer = this.getContainer();

    const oldLaneDate = this.shiveRenderService.getAttribute(
      oldContainer,
      TodoContainerDateAttribute,
    );

    const newLaneDate = this.shiveRenderService.getAttribute(
      newContainer,
      TodoContainerDateAttribute,
    );

    const oldDate = new Date(oldLaneDate);
    const newDate = new Date(newLaneDate);

    return !DateTimeHelper.isSameDay(newDate, oldDate);
  }

  public setContainer(container: HTMLElement, old = false): void {
    const s = this.session;
    if (!s) {
      return;
    }
    if (old) {
      s.oldContainerElement = container;
      return;
    }
    s.currentContainerEl = container;
  }

  public getContainer(old = false): HTMLElement {
    const s = this.session;
    if (!s) {
      return;
    }
    return old ? s.oldContainerElement : s.currentContainerEl;
  }

  public setLaneDate(laneDate: string): void {
    const s = this.session;
    if (!s) {
      return;
    }
    s.currentLaneDate = laneDate;
  }

  public getEntryHeight(): number | null {
    return this.session?.entryHeight ?? null;
  }

  public hasFirstTouchPlaceholder(): boolean {
    return !!this.session?.firstTouchedPlaceholderRef;
  }

  public setFirstTouchPlaceholder(ref: ComponentRef<TodoEntryComponent> | null): void {
    const s = this.session;
    if (!s) {
      return;
    }
    s.firstTouchedPlaceholderRef = ref as any; // better: make it nullable in the session type
  }

  /** Handle lane switch section **/

  public handleLaneSwitch(
    draggableElement: HTMLElement,
    dropzoneElement: HTMLElement, // the drop wrapper
    droppedContainer: HTMLElement,
  ): void {
    // Move all elements in all lanes up and remove the old CSS classes.
    this.resetOldLane();

    // Update the old container in the session compound with new container.
    this.setContainer(droppedContainer, true);

    // Run initialization logic for new lane.
    this.containerEntered(draggableElement, dropzoneElement);
  }

  public resetOldLane(): void {
    const htmlContainer = this.getContainer(true);

    const todoEntries = Array.from(
      htmlContainer.querySelectorAll(
        `.${TodoEntryWrapperCSSClass}:not(.${DragActiveEntryCSSClass})`,
      ),
    ) as HTMLElement[];

    const addTodoBtn = htmlContainer.nextElementSibling as HTMLElement | null;
    if (addTodoBtn) {
      todoEntries.push(addTodoBtn);
    }

    this.todoEntryService.moveElementsUp(todoEntries);

    // reset lane-enter state
    this.setFirstTouchPlaceholder(null);

    const dropzoneElement = this.getContainer(true);
    this.shiveRenderService.removeClass(dropzoneElement, DropTargetCSSClass);
    this.shiveRenderService.removeClass(dropzoneElement, CanDropCSSClass);
  }

  private containerEntered(draggableElement: HTMLElement, dropzoneElement: HTMLElement): void {
    // CSS classes
    this.shiveRenderService.addClass(draggableElement, CanDropCSSClass);
    this.shiveRenderService.addClass(dropzoneElement, DropTargetCSSClass);

    const todoEntries = Array.from(
      dropzoneElement.querySelectorAll(`.${TodoEntryWrapperCSSClass}`),
    ) as HTMLElement[];

    // first touch already set? nothing to do
    if (this.hasFirstTouchPlaceholder()) {
      return;
    }

    // empty lane => animate add button
    if (todoEntries.length === 0) {
      const addTodoBtn = dropzoneElement.querySelector(
        AddToDoButtonWrapperCSSClass,
      ) as HTMLElement | null;
      if (!addTodoBtn) {
        return;
      }

      this.sortableEntriesService.updateAndAnimateEntries(addTodoBtn, -1, this.getEntryHeight());
      return;
    }

    const drRect = draggableElement.getBoundingClientRect();
    const lastTodo = todoEntries[todoEntries.length - 1];
    const lastRect = lastTodo.getBoundingClientRect();

    let startingTodoEntry: TodoEntryModel | undefined;

    // dragged below last => choose last
    if (drRect.bottom > lastRect.bottom) {
      const lastTodoEntryId = Number(this.shiveRenderService.getAttribute(lastTodo, 'id'));
      startingTodoEntry = this.todoEntryCrudService.selectTodoEntry(lastTodoEntryId);
    }

    // otherwise pick most covered by height
    if (!startingTodoEntry) {
      startingTodoEntry = this.pickMostCoveredByHeight(draggableElement, todoEntries);
    }

    assert(!!startingTodoEntry, 'Starting to-do entry is null');

    this.setFirstTouchPlaceholder(startingTodoEntry.componentRef);
  }

  private pickMostCoveredByHeight(
    draggedEl: HTMLElement,
    candidates: HTMLElement[],
  ): TodoEntryModel {
    const draggable = draggedEl.getBoundingClientRect();

    let mostCoveredEl: HTMLElement | null = null;
    let elOverlapY = 0;

    for (const candidate of candidates) {
      const rectangle = candidate.getBoundingClientRect();
      const overlap = this.verticalOverlapPx(draggable, rectangle);

      if (candidate.classList.contains(DragActiveEntryCSSClass)) {
        break;
      }

      if (overlap > elOverlapY) {
        elOverlapY = overlap;
        mostCoveredEl = candidate;
      }
    }

    // If the element doesn't cover any element, just take the most top element in the lane.
    if (!mostCoveredEl) {
      mostCoveredEl = candidates[0];
    }

    const id = +this.shiveRenderService.getAttribute(mostCoveredEl, 'id');
    const mostCoveredTodoEntryModel = this.todoEntryCrudService.selectTodoEntry(id);
    assert(!!mostCoveredTodoEntryModel);

    return mostCoveredTodoEntryModel;
  }

  private verticalOverlapPx(a: DOMRect, b: DOMRect): number {
    const top = Math.max(a.top, b.top);
    const bottom = Math.min(a.bottom, b.bottom);
    return Math.max(0, bottom - top);
  }
}

export type TodoEntryDragSession = {
  draggedCompRef: ComponentRef<TodoEntryComponent>;
  draggedEl: HTMLElement;

  // “ghost” is the placeholder component you remove at the end
  ghostRef: ComponentRef<TodoEntryComponent>;
  firstTouchedPlaceholderRef: ComponentRef<TodoEntryComponent>;

  restrictionEl: HTMLElement;
  oldContainerElement: HTMLElement;
  currentContainerEl: HTMLElement;

  currentLaneDate: string; // yyyy-MM-dd
  entryHeight: number;

  curSortedIndex: number;

  cleanupTimeoutId: number | null;
  dragEnterRafId: number;

  dragInProcess: boolean;
};
